import type { Theme, ThemeOptions } from '@mui/material';
import type { GetTheme } from '../../theme';

import { deepmerge } from '@mui/utils';
import { createTheme } from '@mui/material';

import { shadows } from './token/shadows';
import { colors } from './token/colors';
import { letterSpacing } from './token/sizes';
import { fontFamilyLatoString, getTypography } from './token/typography';
import { hex2rgba } from '../../utils/hex2rgba';
import { latoBold, latoRegular } from '../../fonts/lato';

const finanzcheckThemeOptions: ThemeOptions = {
  palette: colors,
  typography: {
    fontFamily: fontFamilyLatoString,
  },
};

const withFinanzcheckThemeComposition = (theme: Theme): ThemeOptions => ({
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        '@global': {
          '@font-face': [latoRegular, latoBold],
          html: {
            touchAction: 'manipulation',
          },
        },
      },
    },
    MuiFormControlLabel: {
      styleOverrides: {
        label: {
          margin: 0,
        },
      },
    },
    MuiFormHelperText: {
      styleOverrides: {
        root: {
          width: '100%', // This is needed for IE11. Otherwise, it wouldn't wrap the text correctly
        },
        contained: {
          marginLeft: 0,
          paddingLeft: theme.spacing(1),
        },
      },
    },
    MuiFormLabel: {
      styleOverrides: {
        root: {
          color: theme.palette.text.primary,

          '& > .MuiTypography-root': {
            marginBottom: 0,
          },
        },
      },
    },
    MuiLink: {
      styleOverrides: {
        root: {
          textDecoration: 'none',
        },
      },
    },
    MuiTypography: {
      styleOverrides: {
        root: {
          letterSpacing,
        },
        ...getTypography(theme),
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          color: theme.palette.text.primary,
        },
        inputAdornedEnd: {
          paddingRight: 0,
        },
        adornedEnd: {
          paddingRight: 0,
        },
        input: {
          '&:-webkit-autofill,&:-webkit-autofill:hover,&:-webkit-autofill:focus':
            {
              WebkitBoxShadow: shadows.inputOutlined,
              WebkitTextFillColor: theme.palette.text.secondary,
            },
        },
      },
    },
    MuiInputAdornment: {
      styleOverrides: {
        positionEnd: {
          position: 'absolute',
          right: 0,
        },
      },
    },
    MuiAccordion: {
      styleOverrides: {
        root: {
          '&.Mui-disabled': {
            backgroundColor: hex2rgba(colors.input.background, 0.07),
          },
          '&.Mui-expanded': {
            margin: 0,
          },
          // backgroundColor: theme.palette.tertiary.light,
          // color: theme.palette.tertiary.main,
          // borderBottom: `1px solid ${theme.palette.tertiary.main}`,
        },
        rounded: {
          '&:first-child': {
            borderTopLeftRadius: theme.shape.borderRadius,
            borderTopRightRadius: theme.shape.borderRadius,
          },
          '&:last-child': {
            borderBottomLeftRadius: 0,
            borderBottomRightRadius: 0,
          },
        },
      },
    },
    MuiAutocomplete: {
      styleOverrides: {
        root: {
          '&.MuiInputAdornment-positionEnd': {
            position: 'relative',
          },
        },
        inputRoot: {
          paddingRight: '0 !important',
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          color: theme.palette.text.disabled,
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'inherit',
        },
      },
    },
  },
});

export const getTheme: GetTheme = ({
  options,
  optionsWithThemeValues = () => ({}),
}) => {
  let finanzcheckBaseTheme = createTheme(
    deepmerge(finanzcheckThemeOptions, options),
  );
  finanzcheckBaseTheme = createTheme(
    finanzcheckBaseTheme,
    withFinanzcheckThemeComposition(finanzcheckBaseTheme),
  );
  finanzcheckBaseTheme = createTheme(
    finanzcheckBaseTheme,
    optionsWithThemeValues(finanzcheckBaseTheme),
  );

  return finanzcheckBaseTheme;
};
